import { Kunde } from '@gsm/entities';
import { DYNAMIC_FORM_CONTROL_TYPE_GROUP, DYNAMIC_FORM_CONTROL_TYPE_INPUT, DYNAMIC_FORM_CONTROL_TYPE_SELECT, DynamicFormOptionConfig } from '@ng-dynamic-forms/core';
import { compareProps } from '../../helpers/compare-props.helper';
import { getDisplayName } from '../../helpers/display-name.helper';
import { TYPE_SELECT_EDIT } from '../select-edit/select-edit.model';
import { EVENT_TYPE_EDIT_KUNDE } from '../shared-models/custom-events.model';

type CompareFn = (a: any, b: any) => boolean;
export const COMPARE_KUNDEID: CompareFn = (a, b) => compareProps(['kundeId'], a, b);
export const COMPARE_NAMEN: CompareFn = (a, b) => compareProps([
  'vorname',
  'nachname'
], a, b, true);

export const RECHNUNGSSTELLER = {
  kundeId: 'RECHNUNGSSTELLER',
  vorname: 'Rechnungssteller'
} as Kunde;

export const DIREKT_AN_GESCHAEDIGTE_PERSON = {
  kundeId: 'DIREKT_AN_GESCHAEDIGTE_PERSON',
  vorname: 'Direkt an geschädigte Person'
} as Kunde;

export const NEU_EMPFAENGER = {
  kundeId: 'NEU_EMPFAENGER',
  vorname: 'Weiteren Zahlungsempfänger erfassen'
} as Kunde;

const BANK_DETAILS_TEMPLATE = [
  {
    id:            'iban',
    type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
    required:      true,
    label:         'IBAN',
    validators:    {
      required: null,
      iban:     null
    },
    errorMessages: {
      iban: 'Bitte geben Sie ein gültiges Format ein (z.B. AT60 XXXX XXXX XXXX XXXX)'
    }
  },
  {
    id:            'bic',
    type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
    label:         'BIC',
    validators:    {
      bic: null
    },
    errorMessages: {
      bic: 'Bitte geben Sie ein gültiges Format ein (z.B. AAAABBC1D23)'
    }
  }
];

export const ZAHLUNGSEMPFAENGER_TEMPLATE_FACTORY = (
  immediateValidation = false,
  kunden: Kunde[] = [],
  removeRechnungssteller = false,
  addDirektAnGeschPerson = false
) => [
  {
    id:             'zahlungsempfaenger',
    type:           TYPE_SELECT_EDIT,
    label:          'Zahlungsempfänger',
    required:       true,
    eventName:      EVENT_TYPE_EDIT_KUNDE,
    options:        mapKundenToOptions(kunden, removeRechnungssteller, addDirektAnGeschPerson),
    compareWithFn:  (a, b) => {
      return !a || !b ? false : a.kundeId || b.kundeId ? COMPARE_KUNDEID(a, b) : COMPARE_NAMEN(a, b);
    },
    displayLabelFn: getLabelForKunde,
    isEditableFn:   isKundeEditable,
    additional:     {
      immediateValidation
    }
  },
  {
    id:            'bankverbindung',
    type:          DYNAMIC_FORM_CONTROL_TYPE_SELECT,
    required:      true,
    hidden:        true,
    label:         'Bankverbindung',
    compareWithFn: compareProps.bind(null, ['bankverbindungId'])
    // no relations, has to be set visible programmatically
  },
  {
    id:     'externalEmpfaenger',
    type:   DYNAMIC_FORM_CONTROL_TYPE_GROUP,
    hidden: true,
    group:  [
      {
        id:            'vorname',
        type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
        label:         'Vorname',
        validators:    {
          maxLength: 25
        },
        errorMessages: {
          alphanum:  'Bitte geben Sie einen gültigen Vornamen ein',
          maxLength: 'Bitte geben Sie maximal 25 Zeichen ein.'
        }
      },
      {
        id:            'nachname',
        type:          DYNAMIC_FORM_CONTROL_TYPE_INPUT,
        required:      true,
        label:         'Nachname',
        validators:    {
          minLength: 2,
          required:  null,
          maxLength: 60
        },
        errorMessages: {
          minLength: 'Bitte geben Sie einen gültigen Nachnamen ein.',
          alphanum:  'Bitte geben Sie einen gültigen Nachnamen ein.',
          required:  '{{ label }} ist ein Pflichtfeld.',
          maxLength: 'Bitte geben Sie maximal 60 Zeichen ein.'
        }
      },
      ...BANK_DETAILS_TEMPLATE
    ]
    // no relations, has to be set visible programmatically
  },
  {
    id:     'existingEmpfaenger',
    type:   DYNAMIC_FORM_CONTROL_TYPE_GROUP,
    hidden: true,
    group:  BANK_DETAILS_TEMPLATE
    // no relations, has to be set visible programmatically
  }
];

export function mapKundenToOptions(
  kunden: Kunde[] = [],
  removeRechnungssteller: boolean,
  addDirektAnGeschPerson: boolean
): DynamicFormOptionConfig<Kunde>[] {
  return [
    removeRechnungssteller ? null : {value: RECHNUNGSSTELLER},
    addDirektAnGeschPerson ? {value: DIREKT_AN_GESCHAEDIGTE_PERSON} : null,
    ...kunden.map(kunde => ({value: kunde})),
    {value: NEU_EMPFAENGER}
  ].filter(truthy => truthy) as DynamicFormOptionConfig<Kunde>[];
}

function getLabelForKunde(kunde: Kunde): string {
  const {
    vorname,
    nachname,
    namenZusatz
  } = kunde;
  return getDisplayName([
    vorname,
    nachname
  ], [namenZusatz]);
}

function isKundeEditable(kunde: Kunde): boolean {
  return !!(kunde && kunde.kundeId) && ![
    NEU_EMPFAENGER,
    RECHNUNGSSTELLER
  ].some(val => val.kundeId === kunde.kundeId);
}
